import styled from "styled-components";
import { FC, MouseEvent, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import Text from "@/components/Text";
import TableButton from "./TableButton";
import PropsModal from "./PropsModal";
import { ProductionScenePropResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

const SceneNumber = styled(Text)`
  background: ${({ theme }) => theme.colors.lightGray};
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
`;

const TitleHolder = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const Bullet = styled.div`
  display: flex;
  max-width: fit-content;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 50px;
  padding: 3px 8px;
  margin-top: 6px;
  &:first-child {
    margin-top: 16px;
  }
`;

export const BulletKey = styled(Text)`
  white-space: nowrap;
`;

export const BulletValue = styled(Text)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-left: 2px;
`;

interface Props {
  prop: ProductionScenePropResource;
  sceneIndex: number;
}

const PropsTableRow: FC<Props> = ({ prop, sceneIndex }) => {
  const [showModal, setShowModal] = useState(false);

  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this prop?"
      );

      if (confirm) {
        await api.delete<never, { id: number }>(
          `/v1/production/scenes/props/${prop.id}`
        );
        alerts.success("Prop successfully deleted");

        queryClient.invalidateQueries(["production_projects", { projectId }]);
      }
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <TitleHolder>
          <SceneNumber $type="extraSmall">{sceneIndex}</SceneNumber>
          {prop.title}
        </TitleHolder>
        <div>
          {prop.address && (
            <Bullet>
              <BulletKey $type="extraSmall">Location:</BulletKey>
              <BulletValue $type="extraSmall" $color="gray">
                {prop.address}
              </BulletValue>
            </Bullet>
          )}
          {prop.time && (
            <Bullet>
              <BulletKey $type="extraSmall">Date & time:</BulletKey>
              <BulletValue $type="extraSmall" $color="gray">
                {DateTime.fromISO(prop.time).toFormat("dd/LL/yyyy HH:mm")}
              </BulletValue>
            </Bullet>
          )}
        </div>
      </Td>
      <Td>{prop.props}</Td>
      <Td>
        {prop.owner ? (
          <>{prop.owner}</>
        ) : (
          <TableButton text="Add name" onClick={() => setShowModal(true)} />
        )}
      </Td>
      <Td>
        {prop.note ? (
          <>{prop.note}</>
        ) : (
          <TableButton text="Add note" onClick={() => setShowModal(true)} />
        )}
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {!!showModal && (
          <PropsModal prop={prop} onClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default PropsTableRow;
